import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ChevronDownIcon, ChevronUpIcon, ChevronLeftIcon, ChevronRightIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import toast from '../../utils/notify';
import { getLayoutStyle } from './layoutStyles';
import tanikkaiLogo from '../../assets/tanikkai-logo.png';
import './styles/Sidebar.css';

// A module whose status is disabled/blocked for the current customer:
// stays visible in the sidebar but is not navigable.
const LOCKED_TOOLTIP = 'This module is currently disabled.';
const notifyLocked = () => toast(LOCKED_TOOLTIP, { icon: '🔒' });

export const Sidebar = ({
  isOpen,
  onClose,
  navigationItems,
  logoText = 'TanikkAI',
  roleLabel = 'Security Platform',
  onLogout,
  isDarkMode,
  isMobile = false,
  primaryColor = '#2563EB',
  layoutStyle = 'classic',
  sidebarMode: sidebarModeProp,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const location = useLocation();
  const stylePreset = getLayoutStyle(layoutStyle);
  const sidebarPreset = stylePreset.sidebar;
  const isForceDark = sidebarPreset.forceDark;
  const sidebarMode = sidebarModeProp || sidebarPreset.sidebarMode || 'vertical';
  const isHorizontal = sidebarMode === 'horizontal';
  const isCompact = sidebarMode === 'compact';

  // Track which groups are collapsed (none collapsed by default)
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const toggleGroup = (group) => {
    setCollapsedGroups(prev => ({ ...prev, [group]: !prev[group] }));
  };

  // Helper to darken a hex color by a percentage
  const darkenColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = Math.max(0, (num >> 16) - Math.round(255 * percent / 100));
    const g = Math.max(0, ((num >> 8) & 0x00FF) - Math.round(255 * percent / 100));
    const b = Math.max(0, (num & 0x0000FF) - Math.round(255 * percent / 100));
    return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
  };

  // Helper to get rgba from hex
  const hexToRgba = (hex, alpha) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  const darkerColor = darkenColor(primaryColor, 10);

  // Determine display mode — some presets force dark sidebar regardless of theme
  const effectiveDark = isDarkMode || isForceDark;

  const defaultLogoGradient = `linear-gradient(135deg, ${primaryColor} 0%, ${darkerColor} 100%)`;

  // Logo style based on preset
  const getLogoStyle = () => {
    switch (sidebarPreset.logoStyle) {
      case 'solid':
        return { background: primaryColor };
      case 'outlined':
        return {
          background: 'transparent',
          border: `2px solid ${primaryColor}`,
          color: primaryColor,
        };
      case 'gradient':
      default:
        return { background: defaultLogoGradient };
    }
  };

  // Active nav item style based on preset
  const getActiveNavStyle = () => {
    switch (sidebarPreset.activeStyle) {
      case 'solid':
        return {
          background: primaryColor,
          color: 'white',
          borderRadius: sidebarPreset.navItemRadius,
        };
      case 'left-border':
        if (isForceDark) {
          // Branded dark sidebar — white accent so it's visible against the bg
          return {
            background: 'rgba(255,255,255,0.13)',
            borderLeft: '3px solid rgba(255,255,255,0.80)',
            borderRadius: `0 ${sidebarPreset.navItemRadius} ${sidebarPreset.navItemRadius} 0`,
            color: '#FFFFFF',
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
          };
        }
        return {
          background: hexToRgba(primaryColor, effectiveDark ? 0.15 : 0.08),
          borderLeft: `3px solid ${primaryColor}`,
          borderRadius: '0 0.5rem 0.5rem 0',
          color: effectiveDark ? '#F1F5F9' : primaryColor,
        };
      case 'pill':
        return {
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${darkerColor} 100%)`,
          boxShadow: `0 4px 14px ${hexToRgba(primaryColor, 0.35)}`,
          color: 'white',
          borderRadius: '9999px',
        };
      case 'gradient':
      default:
        return {
          background: `linear-gradient(135deg, ${primaryColor} 0%, ${darkerColor} 100%)`,
          boxShadow: `0 4px 12px ${hexToRgba(primaryColor, 0.3)}`,
          color: 'white',
          borderRadius: sidebarPreset.navItemRadius,
        };
    }
  };

  // Sidebar background styles from preset
  // For corporate style, the sidebar background follows primaryColor from platform settings
  const sidebarBg = layoutStyle === 'corporate'
    ? (effectiveDark ? darkenColor(primaryColor, 15) : primaryColor)
    : (effectiveDark ? sidebarPreset.bgDark : sidebarPreset.bg);
  const sidebarBorder = effectiveDark ? sidebarPreset.borderDark : sidebarPreset.border;
  // For corporate style, use white-based text so it looks clear on any primary color background
  const sidebarText = layoutStyle === 'corporate'
    ? '#FFFFFF'
    : (effectiveDark ? sidebarPreset.text : sidebarPreset.text);
  const sidebarTextHover = layoutStyle === 'corporate'
    ? '#FFFFFF'
    : (effectiveDark ? sidebarPreset.textHoverDark : sidebarPreset.textHover);
  const sidebarHoverBg = layoutStyle === 'corporate'
    ? 'rgba(255,255,255,0.10)'
    : (effectiveDark ? sidebarPreset.hoverBgDark : sidebarPreset.hoverBg);

  if (isMobile && !isOpen) return null;

  // ============ HORIZONTAL MODE ============
  if (isHorizontal && !isMobile) {
    return (
      <div
        className={`sidebar-horizontal ${effectiveDark ? 'sidebar-dark' : 'sidebar-light'}`}
        style={{
          background: sidebarBg,
          borderBottomColor: sidebarBorder,
        }}
      >
        {/* Logo */}
        <div className="sidebar-horizontal-logo">
          <div className="sidebar-logo-icon" style={{ overflow: 'hidden' }}>
            <img src={tanikkaiLogo} alt="Tanikkai" className="sidebar-logo-svg" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          <span className="sidebar-horizontal-brand" style={{ color: effectiveDark ? '#F8FAFC' : '#0F172A' }}>
            {logoText}
          </span>
        </div>

        {/* Horizontal Nav Items */}
        <nav className="sidebar-horizontal-nav">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            if (item.locked) {
              return (
                <div
                  key={item.name}
                  onClick={notifyLocked}
                  aria-disabled="true"
                  className="sidebar-horizontal-item sidebar-nav-item-locked"
                  style={{ color: sidebarText, opacity: 0.45, cursor: 'not-allowed' }}
                  title={`${item.name} — ${LOCKED_TOOLTIP}`}
                >
                  {Icon && <Icon className="sidebar-horizontal-icon" />}
                  <span className="sidebar-horizontal-text">{item.name}</span>
                  <LockClosedIcon style={{ width: '0.85rem', height: '0.85rem', marginLeft: '0.35rem' }} />
                </div>
              );
            }
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-horizontal-item ${isActive ? 'sidebar-horizontal-item-active' : ''}`}
                style={
                  isActive
                    ? { color: primaryColor, borderBottomColor: primaryColor }
                    : { color: sidebarText }
                }
                onMouseEnter={(e) => {
                  if (!isActive) e.currentTarget.style.color = sidebarTextHover;
                }}
                onMouseLeave={(e) => {
                  if (!isActive) e.currentTarget.style.color = sidebarText;
                }}
                title={item.name}
              >
                {Icon && <Icon className="sidebar-horizontal-icon" />}
                <span className="sidebar-horizontal-text">{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </div>
    );
  }

  // ============ COMPACT MODE (icon-only sidebar) ============
  if (isCompact && !isMobile) {
    return (
      <div
        className={`sidebar sidebar-compact ${effectiveDark ? 'sidebar-dark' : 'sidebar-light'}`}
        style={{
          background: sidebarBg,
          borderRightColor: sidebarBorder,
          width: stylePreset.layout.sidebarWidth,
        }}
      >
        {/* Compact Logo */}
        <div className="sidebar-compact-header" style={{ borderBottomColor: sidebarBorder }}>
          <div className="sidebar-logo-icon" style={{ width: 36, height: 36, overflow: 'hidden' }}>
            <img src={tanikkaiLogo} alt="Tanikkai" className="sidebar-logo-svg" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        </div>

        {/* Compact Navigation — icons only with tooltips */}
        <nav className="sidebar-compact-nav">
          {navigationItems.map((item) => {
            const isActive = location.pathname === item.href;
            const Icon = item.icon;
            if (item.locked) {
              return (
                <div
                  key={item.name}
                  onClick={notifyLocked}
                  aria-disabled="true"
                  className="sidebar-compact-item sidebar-nav-item-locked"
                  style={{ color: sidebarText, opacity: 0.45, cursor: 'not-allowed', borderRadius: sidebarPreset.navItemRadius }}
                  title={`${item.name} — ${LOCKED_TOOLTIP}`}
                >
                  {Icon && <Icon className="sidebar-compact-icon" />}
                </div>
              );
            }
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-compact-item ${isActive ? 'sidebar-compact-item-active' : ''}`}
                style={
                  isActive
                    ? {
                        background: primaryColor,
                        color: 'white',
                        borderRadius: sidebarPreset.navItemRadius,
                      }
                    : { color: sidebarText, borderRadius: sidebarPreset.navItemRadius }
                }
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = sidebarHoverBg;
                    e.currentTarget.style.color = sidebarTextHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = sidebarText;
                  }
                }}
                title={item.name}
              >
                {Icon && <Icon className="sidebar-compact-icon" />}
              </Link>
            );
          })}
        </nav>
      </div>
    );
  }

  // ============ DEFAULT VERTICAL / FLOATING ============
  const sidebarContent = (
    <div
      className={`sidebar sidebar-layout-${layoutStyle} ${effectiveDark ? 'sidebar-dark' : 'sidebar-light'}`}
      style={{
        background: sidebarBg,
        borderRightColor: sidebarBorder,
        width: isMobile ? undefined : '100%',
      }}
    >
      {/* Logo Section */}
      <div
        className="sidebar-header"
        style={{
          borderBottomColor: sidebarBorder,
          justifyContent: isCollapsed ? 'center' : 'space-between',
          padding: isCollapsed ? '0.625rem 0' : undefined,
        }}
      >
        <div className="sidebar-logo" style={{ gap: isCollapsed ? 0 : undefined }}>
          <div className="sidebar-logo-icon" style={{ overflow: 'hidden' }}>
            <img src={tanikkaiLogo} alt="Tanikkai" className="sidebar-logo-svg" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
          {!isCollapsed && (
            <div className="sidebar-logo-info">
              <span className="sidebar-logo-name">{logoText}</span>
              <span className="sidebar-logo-tagline" style={{ color: layoutStyle === 'corporate' ? 'rgba(255,255,255,0.75)' : (effectiveDark ? '#c7ccd2' : '#3e4c60') }}>
                {roleLabel}
              </span>
            </div>
          )}
        </div>

        {/* Collapse toggle — desktop only */}
        {!isMobile && onToggleCollapse && !isCollapsed && (
          <button
            onClick={onToggleCollapse}
            title="Collapse sidebar"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '0.375rem',
              color: effectiveDark ? '#475569' : '#94A3B8',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 150ms ease, background 150ms ease',
              flexShrink: 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = effectiveDark ? '#94A3B8' : '#475569'; e.currentTarget.style.background = effectiveDark ? '#334155' : '#F1F5F9'; }}
            onMouseLeave={e => { e.currentTarget.style.color = effectiveDark ? '#475569' : '#94A3B8'; e.currentTarget.style.background = 'none'; }}
          >
            <ChevronLeftIcon style={{ width: '1rem', height: '1rem' }} />
          </button>
        )}

        {isMobile && (
          <button className="sidebar-close-btn" onClick={onClose} aria-label="Close sidebar">
            <XMarkIcon className="sidebar-close-icon" />
          </button>
        )}
      </div>

      {/* Expand toggle — shown below logo when collapsed */}
      {!isMobile && onToggleCollapse && isCollapsed && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '0.375rem 0 0.25rem' }}>
          <button
            onClick={onToggleCollapse}
            title="Expand sidebar"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '0.25rem',
              borderRadius: '0.375rem',
              color: effectiveDark ? '#475569' : '#94A3B8',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 150ms ease, background 150ms ease',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = effectiveDark ? '#94A3B8' : '#475569'; e.currentTarget.style.background = effectiveDark ? '#334155' : '#F1F5F9'; }}
            onMouseLeave={e => { e.currentTarget.style.color = effectiveDark ? '#475569' : '#94A3B8'; e.currentTarget.style.background = 'none'; }}
          >
            <ChevronRightIcon style={{ width: '1rem', height: '1rem' }} />
          </button>
        </div>
      )}

      {/* Navigation */}
      <nav className="sidebar-nav">
        <div className="sidebar-nav-list">
          {(() => {
            let lastGroup = null;
            return navigationItems.map((item) => {
              const isActive = location.pathname === item.href;
              const isLocked = !!item.locked;
              const Icon = item.icon;
              const showGroupLabel = !isCollapsed && item.group && item.group !== lastGroup;
              if (item.group) lastGroup = item.group;
              const isGroupCollapsed = !isCollapsed && item.group ? !!collapsedGroups[item.group] : false;
              const isGroupActive = item.group
                ? navigationItems.some(n => n.group === item.group && location.pathname === n.href)
                : false;

              return (
                <React.Fragment key={item.name}>
                  {showGroupLabel && (
                    <button
                      onClick={() => toggleGroup(item.group)}
                      className="sidebar-group-label"
                      data-active={isGroupActive ? 'true' : undefined}
                      style={{
                        fontWeight: isGroupActive ? 700 : 600,
                        color: isGroupActive
                          ? (layoutStyle === 'corporate' ? 'rgba(255,255,255,0.90)' : primaryColor)
                          : (layoutStyle === 'corporate' ? 'rgba(255,255,255,0.60)' : (effectiveDark ? '#475569' : '#94A3B8')),
                        '--app-primary-color': primaryColor,
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = isGroupActive ? (layoutStyle === 'corporate' ? '#FFFFFF' : primaryColor) : (layoutStyle === 'corporate' ? 'rgba(255,255,255,0.80)' : (effectiveDark ? '#94A3B8' : '#64748B'))}
                      onMouseLeave={e => e.currentTarget.style.color = isGroupActive ? (layoutStyle === 'corporate' ? '#FFFFFF' : primaryColor) : (layoutStyle === 'corporate' ? 'rgba(255,255,255,0.60)' : (effectiveDark ? '#475569' : '#94A3B8'))}
                    >
                      <span className="sidebar-group-label-text">{item.group}</span>
                      {collapsedGroups[item.group]
                        ? <ChevronDownIcon className="sidebar-group-label-chevron" />
                        : <ChevronUpIcon   className="sidebar-group-label-chevron" />
                      }
                    </button>
                  )}
                  <AnimatePresence initial={false}>
                    {!isGroupCollapsed && (
                      <motion.div
                        key={item.name + '-wrapper'}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.18, ease: 'easeInOut' }}
                        style={{ overflow: isCollapsed ? 'visible' : 'hidden' }}
                      >
                        {isCollapsed ? (
                          isLocked ? (
                            /* Locked module - visible, icon-only, not navigable */
                            <div
                              onClick={notifyLocked}
                              title={`${item.name} — ${LOCKED_TOOLTIP}`}
                              aria-disabled="true"
                              className="sidebar-nav-item sidebar-collapsed-item sidebar-nav-item-locked"
                              style={{ color: sidebarText, opacity: 0.45, cursor: 'not-allowed', borderRadius: sidebarPreset.navItemRadius, justifyContent: 'center', padding: '0.5rem' }}
                            >
                              {Icon && <Icon className="sidebar-nav-icon" style={{ width: '1.25rem', height: '1.25rem' }} />}
                            </div>
                          ) : (
                          /* Icon-only with native tooltip when collapsed */
                          <Link
                            to={item.href}
                            onClick={() => isMobile && onClose?.()}
                            title={item.name}
                            className={`sidebar-nav-item sidebar-collapsed-item ${isActive ? 'sidebar-nav-item-active' : ''}`}
                            style={
                              isActive
                                ? { ...getActiveNavStyle(), justifyContent: 'center', padding: '0.5rem' }
                                : { color: sidebarText, borderRadius: sidebarPreset.navItemRadius, justifyContent: 'center', padding: '0.5rem' }
                            }
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.background = sidebarHoverBg;
                                e.currentTarget.style.color = sidebarTextHover;
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.background = 'transparent';
                                e.currentTarget.style.color = sidebarText;
                              }
                            }}
                          >
                            {Icon && <Icon className="sidebar-nav-icon" style={{ width: '1.25rem', height: '1.25rem', opacity: 1 }} />}
                          </Link>
                          )
                        ) : (
                          <motion.div
                            whileHover={isLocked ? undefined : { x: 4 }}
                            whileTap={isLocked ? undefined : { scale: 0.98 }}
                            className="sidebar-nav-item-wrapper"
                          >
                            {isLocked ? (
                              /* Locked module - stays in the list, greyed, with a
                                 lock icon on the right, not navigable. */
                              <div
                                onClick={notifyLocked}
                                aria-disabled="true"
                                className="sidebar-nav-item sidebar-nav-item-locked"
                                style={{ color: sidebarText, opacity: 0.45, cursor: 'not-allowed', borderRadius: sidebarPreset.navItemRadius }}
                                title={`${item.name} — ${LOCKED_TOOLTIP}`}
                              >
                                {Icon && <Icon className="sidebar-nav-icon" />}
                                <span className="sidebar-nav-text">{item.name}</span>
                                <LockClosedIcon style={{ width: '0.9rem', height: '0.9rem', marginLeft: 'auto', flexShrink: 0 }} />
                              </div>
                            ) : (
                            <Link
                              to={item.href}
                              onClick={() => isMobile && onClose?.()}
                              data-active={isActive ? 'true' : undefined}
                              className={`sidebar-nav-item ${isActive ? 'sidebar-nav-item-active' : ''}`}
                              style={
                                isActive
                                  ? getActiveNavStyle()
                                  : { color: sidebarText, borderRadius: sidebarPreset.navItemRadius }
                              }
                              onMouseEnter={(e) => {
                                if (!isActive) {
                                  e.currentTarget.style.background = sidebarHoverBg;
                                  e.currentTarget.style.color = sidebarTextHover;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isActive) {
                                  e.currentTarget.style.background = 'transparent';
                                  e.currentTarget.style.color = sidebarText;
                                }
                              }}
                              title={item.name}
                            >
                              {Icon && <Icon className="sidebar-nav-icon" />}
                              <span className="sidebar-nav-text">{item.name}</span>
                            </Link>
                            )}
                          </motion.div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </React.Fragment>
              );
            });
          })()}
        </div>
      </nav>
    </div>
  );

  if (isMobile) {
    return (
      <div className="sidebar-mobile-overlay">
        <motion.div
          initial={{ x: -280 }}
          animate={{ x: 0 }}
          exit={{ x: -280 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="sidebar-mobile-panel"
        >
          {sidebarContent}
        </motion.div>
        <div
          className="sidebar-mobile-backdrop"
          onClick={onClose}
        />
      </div>
    );
  }

  return sidebarContent;
};

export default Sidebar;
